import useRequest from "~/hooks/use-request";
import useUserStore from "~/stores/user";
import { formatUserModel } from "~/utils/user";

const UserAPI = {
  load(): Promise<void> {
    const { request } = useRequest();
    const userStore = useUserStore();

    return request
      .get<IContentResponse<IResponseProfile>>("/api/user/get_profile")
      .then((response) => {
        userStore.user = formatUserModel(response.content);
      });
  },

  update(userData: Partial<IModelUser>): Promise<void> {
    const userStore = useUserStore();

    if (!userStore.isAuthenticated)
      throw new Error("User data not found!");

    const { request } = useRequest();

    const payload: IRequestPayloadUpdateProfile = {
      data: {
        displayName: userData.fullName,
        countryName: userData.countryName,
        phone: userData.phone,
        postalCode: userData.postalCode,
        city: userData.city,
        gender: userData.gender,
        identity: userData.identity,
        identityItem: userData.identityItem,
        dateOfBirth: userData.dateOfBirth,
      },
    };

    return request
      .post<IContentResponse<IResponsePayloadUpdateProfile>>("/api/user/update_profile", { body: payload })
      .then((response) => {
        if (!userStore.user)
          throw new Error("User data not found!");

        userStore.user.fullName = response.content.displayName;
        userStore.user.countryName = response.content.countryName;
        userStore.user.phone = response.content.phone;
        userStore.user.postalCode = response.content.postalCode;
        userStore.user.city = response.content.city;
        userStore.user.gender = response.content.gender;
        userStore.user.options = response.content.options;
      });
  },

  updateSurveyOptions(surveyName = "survey_user_experience_1", newData: Partial<ISurveyOption>) {
    const { request } = useRequest();
    const userStore = useUserStore();

    const existingOptions: ISurveyOptions = userStore.user?.options || {};

    const updatedOptions: ISurveyOptions = {
      ...existingOptions,
      [surveyName]: {
        ...(existingOptions[surveyName] || {}),
        ...newData,
        surveyName,
      },
    };

    const payload = {
      data: {
        options: updatedOptions,
      },
    };

    return request
      .post<IContentResponse<IResponsePayloadUpdateProfile>>("/api/user/update_profile", { body: payload })
      .then((response) => {
        if (!userStore.user)
          throw new Error("User data not found!");
        userStore.user.options = response.content.options;
      });
  },

  skipSurvey(surveyName?: string) {
    return this.updateSurveyOptions(surveyName, { skipped: new Date().toISOString() });
  },

  submitSurvey(surveyName?: string) {
    return this.updateSurveyOptions(surveyName, { submitted: new Date().toISOString() });
  },

  delete() {
    const { request } = useRequest();

    return request.post("/api/user/delete_account");
  },
};

export default UserAPI;
